import { createSlice } from '@reduxjs/toolkit';

// Конфигурация сервисов
const SERVICES_CONFIG = [
  { 
    key: 'universe_gateway', 
    name: 'Universe Gateway', 
    testEndpoints: ['/health', '/status'],
    requiresAuth: true,
    baseUrl: 'http://localhost:4000'
  },
  { 
    key: 'imcon', 
    name: 'ImCon', 
    testEndpoints: ['/health', '/api/health'],
    requiresAuth: false,
    baseUrl: 'http://localhost:4001'
  },
  { 
    key: 'world_manager', 
    name: 'World Manager', 
    testEndpoints: ['/health', '/status'],
    requiresAuth: true,
    baseUrl: 'http://localhost:4002'
  },
  { 
    key: 'sim_character', 
    name: 'Sim Character', 
    testEndpoints: ['/health', '/api/health'],
    requiresAuth: true,
    baseUrl: 'http://localhost:4003'
  },
  { 
    key: 'character_designer', 
    name: 'Character Designer', 
    testEndpoints: ['/health', '/status'],
    requiresAuth: true,
    baseUrl: 'http://localhost:4004'
  }
];

// Начальное состояние для инфраструктурных компонентов
const createInfrastructureState = () => ({
  status: 'unknown',
  message: '',
  lastChecked: null,
  details: null,
  metrics: {}
});

const initialState = {
  // Навигация и UI
  activeTab: 0,
  healthCheckLoading: false,
  
  // Логи
  logs: [],
  logFilters: {
    level: 'all',
    search: '',
    service: 'all',
    expanded: false
  },
  
  // Сервисы
  services: SERVICES_CONFIG,
  networkDiagnostics: {},
  
  // Инфраструктура - отдельные поля для совместимости
  rabbitmqStatus: createInfrastructureState(),
  tarantoolStatus: createInfrastructureState(),
  mongodbStatus: createInfrastructureState(),
  clickhouseStatus: createInfrastructureState(),
  featureTreeStatus: createInfrastructureState(),
  
  // Инфраструктура - структурированная
  infrastructure: {
    rabbitmq: createInfrastructureState(),
    tarantool: createInfrastructureState(),
    mongodb: createInfrastructureState(),
    clickhouse: createInfrastructureState(),
    featureTree: createInfrastructureState(),
    kratos: {
      public: createInfrastructureState(),
      admin: createInfrastructureState(),
      users: createInfrastructureState()
    }
  },
  
  // REST API тестер
  selectedService: 'universe_gateway',
  restPath: '/health',
  restMethod: 'GET',
  restBody: '',
  restResponse: null,
  restLoading: false,
  
  // Настройки
  settings: {
    autoRefresh: false,
    autoRefreshInterval: 30000,
    timeout: 8000,
    maxLogs: 100
  },
  
  // Статистика
  statistics: {
    lastCheck: null,
    totalChecks: 0,
    successRate: 0,
    averageResponseTime: 0
  }
};

const servicesTesterSlice = createSlice({
  name: 'servicesTester',
  initialState,
  reducers: {
    // === НАВИГАЦИЯ И UI ===
    setActiveTab: (state, action) => {
      state.activeTab = action.payload;
    },
    
    // === СОСТОЯНИЕ ЗАГРУЗКИ ===
    setHealthCheckLoading: (state, action) => {
      state.healthCheckLoading = action.payload;
    },
    
    setRestLoading: (state, action) => {
      state.restLoading = action.payload;
    },
    
    // === ЛОГИ ===
    addLog: (state, action) => {
      const { 
        msg, 
        type = 'info', 
        data = {}, 
        service = 'system',
        timestamp = new Date().toISOString(), 
        id = Date.now() + Math.random() 
      } = action.payload;
      
      const logEntry = {
        id,
        msg,
        type,
        data,
        service,
        timestamp
      };
      
      state.logs.unshift(logEntry);
      
      // Ограничиваем количество логов
      if (state.logs.length > state.settings.maxLogs) {
        state.logs = state.logs.slice(0, state.settings.maxLogs);
      }
      
      // Обновляем статистику
      state.statistics.lastCheck = timestamp;
      state.statistics.totalChecks += 1;
    },
    
    clearLogs: (state) => {
      state.logs = [];
    },
    
    updateLogFilters: (state, action) => {
      state.logFilters = { ...state.logFilters, ...action.payload };
    },
    
    // === СЕРВИСЫ И ДИАГНОСТИКА ===
    updateNetworkDiagnostics: (state, action) => {
      const { serviceKey, diagnostics } = action.payload;
      state.networkDiagnostics[serviceKey] = {
        ...diagnostics,
        lastChecked: new Date().toISOString()
      };
    },
    
    clearNetworkDiagnostics: (state) => {
      state.networkDiagnostics = {};
    },
    
    updateServiceStatus: (state, action) => {
      const { serviceKey, status, responseTime, error } = action.payload;
      if (state.networkDiagnostics[serviceKey]) {
        state.networkDiagnostics[serviceKey] = {
          ...state.networkDiagnostics[serviceKey],
          status,
          responseTime,
          error,
          lastChecked: new Date().toISOString()
        };
      }
    },
    
    // === ИНФРАСТРУКТУРА - ОБЩИЕ ===
    updateInfrastructureStatus: (state, action) => {
      const { component, status, message, details, metrics } = action.payload;
      
      if (state.infrastructure[component]) {
        state.infrastructure[component] = {
          status: status || 'unknown',
          message: message || '',
          details: details || null,
          metrics: metrics || {},
          lastChecked: new Date().toISOString()
        };
      }
    },
    
    // === ИНФРАСТРУКТУРА - RABBITMQ ===
    setRabbitMQStatus: (state, action) => {
      const status = {
        ...action.payload,
        lastChecked: new Date().toISOString()
      };
      state.rabbitmqStatus = status;
      state.infrastructure.rabbitmq = status;
    },
    
    updateRabbitMQMetrics: (state, action) => {
      const { queues, connections, messages, exchanges } = action.payload;
      const metrics = {
        queues: queues || state.infrastructure.rabbitmq.metrics.queues,
        connections: connections || state.infrastructure.rabbitmq.metrics.connections,
        messages: messages || state.infrastructure.rabbitmq.metrics.messages,
        exchanges: exchanges || state.infrastructure.rabbitmq.metrics.exchanges
      };
      
      state.rabbitmqStatus.metrics = metrics;
      state.infrastructure.rabbitmq.metrics = metrics;
    },
    
    // === ИНФРАСТРУКТУРА - TARANTOOL ===
    setTarantoolStatus: (state, action) => {
      const status = {
        ...action.payload,
        lastChecked: new Date().toISOString()
      };
      state.tarantoolStatus = status;
      state.infrastructure.tarantool = status;
    },
    
    updateTarantoolMetrics: (state, action) => {
      const { connections, memory, requests } = action.payload;
      const metrics = {
        connections: connections || state.infrastructure.tarantool.metrics.connections,
        memory: memory || state.infrastructure.tarantool.metrics.memory,
        requests: requests || state.infrastructure.tarantool.metrics.requests
      };
      
      state.tarantoolStatus.metrics = metrics;
      state.infrastructure.tarantool.metrics = metrics;
    },
    
    // === ИНФРАСТРУКТУРА - MONGODB ===
    setMongoDBStatus: (state, action) => {
      const status = {
        ...action.payload,
        lastChecked: new Date().toISOString()
      };
      state.mongodbStatus = status;
      state.infrastructure.mongodb = status;
    },
    
    updateMongoDBMetrics: (state, action) => {
      const { databases, collections, connections, storage } = action.payload;
      const metrics = {
        databases: databases || state.infrastructure.mongodb.metrics.databases,
        collections: collections || state.infrastructure.mongodb.metrics.collections,
        connections: connections || state.infrastructure.mongodb.metrics.connections,
        storage: storage || state.infrastructure.mongodb.metrics.storage
      };
      
      state.mongodbStatus.metrics = metrics;
      state.infrastructure.mongodb.metrics = metrics;
    },
    
    // === ИНФРАСТРУКТУРА - CLICKHOUSE ===
    setClickHouseStatus: (state, action) => {
      const status = {
        ...action.payload,
        lastChecked: new Date().toISOString()
      };
      state.clickhouseStatus = status;
      state.infrastructure.clickhouse = status;
    },
    
    updateClickHouseMetrics: (state, action) => {
      const { version, tables, events_count, unique_events } = action.payload;
      const metrics = {
        version: version || state.infrastructure.clickhouse.metrics.version,
        tables: tables || state.infrastructure.clickhouse.metrics.tables,
        events_count: events_count || state.infrastructure.clickhouse.metrics.events_count,
        unique_events: unique_events || state.infrastructure.clickhouse.metrics.unique_events
      };
      
      state.clickhouseStatus.metrics = metrics;
      state.infrastructure.clickhouse.metrics = metrics;
    },
    
    // === ИНФРАСТРУКТУРА - FEATURE TREE ===
    setFeatureTreeStatus: (state, action) => {
      const status = {
        ...action.payload,
        lastChecked: new Date().toISOString()
      };
      state.featureTreeStatus = status;
      state.infrastructure.featureTree = status;
    },
    
    updateFeatureTreeMetrics: (state, action) => {
      const { 
        totalNodes, 
        version, 
        generationTime, 
        ontologyPath,
        lastGenerated 
      } = action.payload;
      
      const metrics = {
        totalNodes: totalNodes || state.infrastructure.featureTree.metrics.totalNodes,
        version: version || state.infrastructure.featureTree.metrics.version,
        generationTime: generationTime || state.infrastructure.featureTree.metrics.generationTime,
        ontologyPath: ontologyPath || state.infrastructure.featureTree.metrics.ontologyPath,
        lastGenerated: lastGenerated || state.infrastructure.featureTree.metrics.lastGenerated
      };
      
      state.featureTreeStatus.metrics = metrics;
      state.infrastructure.featureTree.metrics = metrics;
    },
    
    // === ИНФРАСТРУКТУРА - KRATOS ===
    setKratosPublicStatus: (state, action) => {
      state.infrastructure.kratos.public = {
        ...state.infrastructure.kratos.public,
        ...action.payload,
        lastChecked: new Date().toISOString()
      };
    },
    
    setKratosAdminStatus: (state, action) => {
      state.infrastructure.kratos.admin = {
        ...state.infrastructure.kratos.admin,
        ...action.payload,
        lastChecked: new Date().toISOString()
      };
    },
    
    setKratosUsersStatus: (state, action) => {
      state.infrastructure.kratos.users = {
        ...state.infrastructure.kratos.users,
        ...action.payload,
        lastChecked: new Date().toISOString()
      };
    },
    
    updateKratosMetrics: (state, action) => {
      const { totalUsers, activeUsers, inactiveUsers, sessions } = action.payload;
      state.infrastructure.kratos.users.metrics = {
        totalUsers: totalUsers || state.infrastructure.kratos.users.metrics.totalUsers,
        activeUsers: activeUsers || state.infrastructure.kratos.users.metrics.activeUsers,
        inactiveUsers: inactiveUsers || state.infrastructure.kratos.users.metrics.inactiveUsers,
        sessions: sessions || state.infrastructure.kratos.users.metrics.sessions
      };
    },
    
    // === REST API ТЕСТЕР ===
    setSelectedService: (state, action) => {
      state.selectedService = action.payload;
    },
    
    setRestPath: (state, action) => {
      state.restPath = action.payload;
    },
    
    setRestMethod: (state, action) => {
      state.restMethod = action.payload;
    },
    
    setRestBody: (state, action) => {
      state.restBody = action.payload;
    },
    
    setRestResponse: (state, action) => {
      state.restResponse = action.payload;
    },
    
    clearRestResponse: (state) => {
      state.restResponse = null;
    },
    
    // === НАСТРОЙКИ ===
    updateSettings: (state, action) => {
      state.settings = { ...state.settings, ...action.payload };
    },
    
    setAutoRefresh: (state, action) => {
      state.settings.autoRefresh = action.payload;
    },
    
    setAutoRefreshInterval: (state, action) => {
      state.settings.autoRefreshInterval = action.payload;
    },
    
    // === СТАТИСТИКА ===
    updateStatistics: (state, action) => {
      state.statistics = { ...state.statistics, ...action.payload };
    },
    
    incrementTotalChecks: (state) => {
      state.statistics.totalChecks += 1;
    },
    
    updateSuccessRate: (state, action) => {
      state.statistics.successRate = action.payload;
    },
    
    updateAverageResponseTime: (state, action) => {
      state.statistics.averageResponseTime = action.payload;
    },
    
    // === МАССОВЫЕ ОПЕРАЦИИ ===
    updateMultipleInfrastructureStatuses: (state, action) => {
      const statuses = action.payload;
      Object.entries(statuses).forEach(([component, status]) => {
        if (state.infrastructure[component]) {
          state.infrastructure[component] = {
            ...state.infrastructure[component],
            ...status,
            lastChecked: new Date().toISOString()
          };
        }
      });
    },
    
    updateAllServiceDiagnostics: (state, action) => {
      const diagnostics = action.payload;
      Object.entries(diagnostics).forEach(([serviceKey, diagnostic]) => {
        state.networkDiagnostics[serviceKey] = {
          ...diagnostic,
          lastChecked: new Date().toISOString()
        };
      });
    },
    
    // === СБРОС СОСТОЯНИЯ ===
    resetLogs: (state) => {
      state.logs = [];
    },
    
    resetDiagnostics: (state) => {
      state.networkDiagnostics = {};
    },
    
    resetInfrastructure: (state) => {
      state.rabbitmqStatus = createInfrastructureState();
      state.tarantoolStatus = createInfrastructureState();
      state.mongodbStatus = createInfrastructureState();
      state.clickhouseStatus = createInfrastructureState();
      state.featureTreeStatus = createInfrastructureState();
      state.infrastructure = initialState.infrastructure;
    },
    
    resetRestTester: (state) => {
      state.selectedService = initialState.selectedService;
      state.restPath = initialState.restPath;
      state.restMethod = initialState.restMethod;
      state.restBody = initialState.restBody;
      state.restResponse = initialState.restResponse;
      state.restLoading = initialState.restLoading;
    },
    
    resetStatistics: (state) => {
      state.statistics = initialState.statistics;
    },
    
    resetAll: () => initialState
  }
});

// Селекторы для удобства использования
export const servicesTesterSelectors = {
  // Основные селекторы
  selectActiveTab: (state) => state.servicesTester.activeTab,
  selectHealthCheckLoading: (state) => state.servicesTester.healthCheckLoading,
  selectLogs: (state) => state.servicesTester.logs,
  selectLogFilters: (state) => state.servicesTester.logFilters,
  
  // Селекторы сервисов
  selectServices: (state) => state.servicesTester.services,
  selectNetworkDiagnostics: (state) => state.servicesTester.networkDiagnostics,
  selectServiceDiagnostics: (serviceKey) => (state) => 
    state.servicesTester.networkDiagnostics[serviceKey],
  
  // Селекторы инфраструктуры - отдельные поля для совместимости
  selectRabbitMQStatus: (state) => state.servicesTester.rabbitmqStatus,
  selectTarantoolStatus: (state) => state.servicesTester.tarantoolStatus,
  selectMongoDBStatus: (state) => state.servicesTester.mongodbStatus,
  selectClickHouseStatus: (state) => state.servicesTester.clickhouseStatus,
  selectFeatureTreeStatus: (state) => state.servicesTester.featureTreeStatus,
  
  // Селекторы инфраструктуры - структурированные
  selectInfrastructure: (state) => state.servicesTester.infrastructure,
  selectKratosStatus: (state) => state.servicesTester.infrastructure.kratos,
  
  // Селекторы REST тестера
  selectSelectedService: (state) => state.servicesTester.selectedService,
  selectRestPath: (state) => state.servicesTester.restPath,
  selectRestMethod: (state) => state.servicesTester.restMethod,
  selectRestBody: (state) => state.servicesTester.restBody,
  selectRestResponse: (state) => state.servicesTester.restResponse,
  selectRestLoading: (state) => state.servicesTester.restLoading,
  
  // Селекторы настроек
  selectSettings: (state) => state.servicesTester.settings,
  selectAutoRefresh: (state) => state.servicesTester.settings.autoRefresh,
  
  // Селекторы статистики
  selectStatistics: (state) => state.servicesTester.statistics,
  
  // Вычисляемые селекторы
  selectHealthyServicesCount: (state) => {
    const diagnostics = state.servicesTester.networkDiagnostics;
    return Object.values(diagnostics).filter(d => d && d.status === 'healthy').length;
  },
  
  selectTotalServicesCount: (state) => state.servicesTester.services.length,
  
  selectInfrastructureHealth: (state) => {
    const infra = state.servicesTester.infrastructure;
    const components = [infra.rabbitmq, infra.tarantool, infra.mongodb, infra.featureTree];
    const healthy = components.filter(c => 
      c && (c.status === 'connected' || c.status === 'success' || c.status === 'healthy')
    ).length;
    return { healthy, total: components.length };
  },
  
  selectFilteredLogs: (filters) => (state) => {
    const { level, search, service } = filters || state.servicesTester.logFilters;
    let logs = state.servicesTester.logs;
    
    if (level !== 'all') {
      logs = logs.filter(log => log.type === level);
    }
    
    if (service !== 'all') {
      logs = logs.filter(log => log.service === service);
    }
    
    if (search) {
      const searchLower = search.toLowerCase();
      logs = logs.filter(log => 
        log.msg.toLowerCase().includes(searchLower) ||
        (log.data && JSON.stringify(log.data).toLowerCase().includes(searchLower))
      );
    }
    
    return logs;
  }
};

// Экспортируем все экшены
export const {
  // Навигация и UI
  setActiveTab,
  
  // Состояние загрузки
  setHealthCheckLoading,
  setRestLoading,
  
  // Логи
  addLog,
  clearLogs,
  updateLogFilters,
  
  // Сервисы и диагностика
  updateNetworkDiagnostics,
  clearNetworkDiagnostics,
  updateServiceStatus,
  
  // Инфраструктура - общие
  updateInfrastructureStatus,
  updateMultipleInfrastructureStatuses,
  
  // Инфраструктура - RabbitMQ
  setRabbitMQStatus,
  updateRabbitMQMetrics,
  
  // Инфраструктура - Tarantool
  setTarantoolStatus,
  updateTarantoolMetrics,
  
  // Инфраструктура - MongoDB
  setMongoDBStatus,
  updateMongoDBMetrics,
  
  // Инфраструктура - ClickHouse
  setClickHouseStatus,
  updateClickHouseMetrics,
  
  // Инфраструктура - Feature Tree
  setFeatureTreeStatus,
  updateFeatureTreeMetrics,
  
  // Инфраструктура - Kratos
  setKratosPublicStatus,
  setKratosAdminStatus,
  setKratosUsersStatus,
  updateKratosMetrics,
  
  // REST API тестер
  setSelectedService,
  setRestPath,
  setRestMethod,
  setRestBody,
  setRestResponse,
  clearRestResponse,
  
  // Настройки
  updateSettings,
  setAutoRefresh,
  setAutoRefreshInterval,
  
  // Статистика
  updateStatistics,
  incrementTotalChecks,
  updateSuccessRate,
  updateAverageResponseTime,
  
  // Массовые операции
  updateAllServiceDiagnostics,
  
  // Сброс состояния
  resetLogs,
  resetDiagnostics,
  resetInfrastructure,
  resetRestTester,
  resetStatistics,
  resetAll
} = servicesTesterSlice.actions;

export default servicesTesterSlice.reducer;
